import type { Member } from "@/types/Member";

/**
 * Check if a member is elevated (admin, practice manager, or superadmin)
 * Elevated users have special permissions such as booking on behalf of others and booking in the past
 */
export function isElevatedUser(member: Member | null | undefined): boolean {
  if (!member || !member.role) return false;

  return (
    member.role.includes("admin") ||
    member.role.includes("superadmin") ||
    member.role.includes("practice_manager")
  );
}

/**
 * Check if a member can make bookings in the past
 * Only elevated users can make past bookings
 */
export function canMakePastBookings(member: Member | null | undefined): boolean {
  return isElevatedUser(member);
}

/**
 * Check if a member is an admin OR a superadmin. Use this for gates that should accept
 * org-wide administrators only (not practice managers).
 *
 * Direct `member.role.includes("admin")` checks silently exclude superadmins, because
 * `role` is an array of role tokens — `["superadmin"].includes("admin")` is `false`.
 */
export function isAdminOrSuperadmin(member: Member | null | undefined): boolean {
  if (!member || !member.role) return false;
  return member.role.includes("admin") || member.role.includes("superadmin");
}

/**
 * Whether `member` may waive/re-charge (toggle gratis) on a booking at `locationId`.
 *
 * Admins and superadmins may do so anywhere. A practice manager may do so ONLY at a centre they
 * manage — this MIRRORS the server gate (`bookingRoutes` requires the `pm:<locationId>` role
 * token). The generic `isElevatedUser` check is deliberately NOT used here: it accepts any
 * `practice_manager`, which showed the "Remove charges" control to PMs who don't manage the centre
 * and then 403'd them ("Only practice managers or admins can toggle gratis status"). We accept the
 * location either via the server-parity `pm:<locationId>` token or the frontend's
 * `permissableLocationIds` list (belt-and-braces, since both describe a PM's managed centres).
 */
export function canManageLocationCharges(member: Member | null | undefined, locationId: string | null | undefined): boolean {
  if (isAdminOrSuperadmin(member)) return true;
  if (!member?.role?.includes("practice_manager")) return false;
  if (!locationId) return false;
  return member.role.includes(`pm:${locationId}`) || (member.permissableLocationIds ?? []).includes(locationId);
}